import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { userActions } from '../redux/actions';
import { SEO } from '../utils';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import Pagination from 'react-js-pagination';

import { PageNotFound } from '.';
import { startLoader } from '../components/common/Loader';
import { CustomInput } from '../components';
import { MdSearch } from 'react-icons/md';

import '../assets/styles/pages/_search.scss';

const SearchValidationSchema = Yup.object({
  search: Yup.string().required('The search bar is empty!'),
  limit: Yup.string(),
});

const Search = ({ history, match, search, ...props }) => {
  const [params, setParams] = useState(
    new URLSearchParams(history.location.search)
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(5);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    startLoader();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const pageNumber = Number(params.get('page'));
    const limit = Number(params.get('limit'));
    const searchKeyword = params.get('search');
    pageNumber && setPageNumber(pageNumber);
    limit && setLimit(limit);
    searchKeyword && setSearchKeyword(searchKeyword);
    params && setParams(params);
  }, [history.location.search]);

  useEffect(() => {
    props.getMeta('Search');
    setLoading(true);
    props.getSearchResults(history.location.search).then((response) => {
      setTimeout(() => setLoading(false), 3000);
    });
  }, [history.location.search]);

  const handlePageChange = (page) => {
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth',
    });
    setTimeout(() => {
      if (page !== pageNumber) {
        history.push(`?page=${page}&limit=${limit}&search=${searchKeyword}`);
        setPageNumber(page);
      }
    }, 300);
  };

  const handleSubmit = (page = 1, limit = 5, search = '') => {
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth',
    });
    setTimeout(
      () =>
        history.push(
          `/search?page=${page}&limit=${limit}&search=${
            search === null ? '' : search
          }`
        ),
      300
    );
  };

  const getTotalRange = (searchTotal) => {
    const page = params.get('page') && Number(params.get('page'));
    const limit = params.get('limit') && Number(params.get('limit'));
    const total = searchTotal && Number(searchTotal);
    let range = '';

    if (total === 0) {
      range = `${total}`;
    } else if (page === 1) {
      range = `${page} - ${limit}`;
    } else {
      const from = (page - 1) * limit + 1;
      const to = limit * page;
      if (from === to || (from !== to && to > total)) {
        range = `${total}`;
      } else if (from !== to && to > total) {
        range = `${from} - ${total}`;
      } else {
        range = `${from} - ${to}`;
      }
    }

    return range;
  };

  const totalRange = getTotalRange(
    search && search?.items && search?.items.total
  );
  const metaData = props.metaData?.res;
  return props.search?.error ? (
    <PageNotFound loader={false} />
  ) : (
    <>
      <SEO
        title={metaData && metaData.title}
        description={metaData && metaData.description}
        img={metaData && metaData.image}
        keywords={metaData && metaData.key}
      />

      <div className='search'>
        <Link to='/' className='banner-logo search-logo'>
          <img src='/media/images/InClassicaColorLogo.png' alt='logo' />
        </Link>
        <Formik
          initialValues={{
            limit: params.get('limit') || '',
            search: params.get('search') || '',
          }}
          setField
          validationSchema={SearchValidationSchema}
          onSubmit={(e) => {
            handleSubmit(1, e.limit ? e.limit : 5, e.search);
          }}
        >
          {({ values }) => (
            <Form className='search__form'>
              <CustomInput
                name='search'
                label='Search'
                autoFocus
                fieldStylesClass='search__form__field'
                labelStylesClass='search__form__field__label'
                inputStylesClass='search__form__field__input'
                errorStylesClass='search__form__field__error'
                id='searchPageInput'
              />
              <button type='submit' className='search__form__submit'>
                <MdSearch />
              </button>
              <CustomInput
                name='limit'
                label='Results per page'
                inputComponentType='select'
                fieldStylesClass='search__form__select'
                labelStylesClass='search__form__select__label'
                inputStylesClass='search__form__select__input'
                errorStylesClass='search__form__select__error'
                value={limit}
                handleFieldChange={(e) =>
                  handleSubmit(pageNumber, e.target.value, values.search)
                }
              >
                <option value='5'>5</option>
                <option value='10'>10</option>
                <option value='15'>15</option>
                <option value='25'>25</option>
                <option value='40'>40</option>
              </CustomInput>
            </Form>
          )}
        </Formik>

        <div className='search__results'>
          {loading ? (
            <div className='search__results__loading'>
              <p className='search__results__loading__total' />

              <div className='result__loading'>
                <p className='result__loading__page' />
                <h2 className='result__loading__title' />
                <p className='result__loading__description' />
              </div>
              <div className='result__loading'>
                <p className='result__loading__page' />
                <h2 className='result__loading__title' />
                <p className='result__loading__description' />
              </div>
              <div className='result__loading'>
                <p className='result__loading__page' />
                <h2 className='result__loading__title' />
                <p className='result__loading__description' />
              </div>
            </div>
          ) : (
            search &&
            search?.items && (
              <>
                <p className='search__results__total'>
                  {totalRange !== '0' ? (
                    <>
                      Showing{' '}
                      <strong>
                        {totalRange === String(search?.items.total)
                          ? totalRange
                          : `from ${totalRange}`}
                      </strong>
                      , found total of <strong>{search?.items.total}</strong>
                      {search?.items.total > 1 ? ' matches' : ' match'}
                    </>
                  ) : (
                    <> Nothing was found </>
                  )}
                </p>

                <div className='search__results__pagination'>
                  <Pagination
                    hideFirstLastPages
                    hideNavigation
                    activeClass='active'
                    activePage={pageNumber}
                    itemsCountPerPage={limit}
                    totalItemsCount={search?.items.total}
                    pageRangeDisplayed={6}
                    onChange={(e) => handlePageChange(e)}
                  />
                </div>
                {search?.items.data.map((res) => (
                  <a
                    href={res.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    key={res.url}
                    className='result'
                  >
                    <p className='result__page'>
                      {res.url
                        .split('/')[1]
                        .replace('/', '')
                        .replace(/#.*/, '') === 'calendar'
                        ? 'concert'
                        : res.url
                            .split('/')[1]
                            .replace('/', '')
                            .replace(/#.*/, '')}{' '}
                      page
                    </p>
                    <h2
                      className='result__title'
                      dangerouslySetInnerHTML={{ __html: res.title }}
                    />
                    <p
                      className='result__description'
                      dangerouslySetInnerHTML={{ __html: res.description }}
                    />
                  </a>
                ))}

                <div className='search__results__pagination'>
                  <Pagination
                    hideFirstLastPages
                    hideNavigation
                    activeClass='active'
                    activePage={pageNumber}
                    itemsCountPerPage={limit}
                    totalItemsCount={search?.items.total}
                    pageRangeDisplayed={6}
                    onChange={(e) => handlePageChange(e)}
                  />
                </div>
              </>
            )
          )}
        </div>
      </div>
    </>
  );
};

function mapState (state) {
  return {
    search: state.getSearchResults,
    metaData: state.getMeta,
  };
}
const actionCreators = {
  getSearchResults: userActions.getSearchResults,
  getMeta: userActions.getMetaData,
};

Search.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  search: PropTypes.object,
  getSearchResults: PropTypes.func,
};
Search.fetchMeta = () => actionCreators.getMeta('Search');
Search.serverFetch = () => actionCreators.getSearchResults();

export default connect(mapState, actionCreators)(Search);
